import React from 'react';

import { Alert, AlertTitle, Button, Chip, Link, Stack, Typography } from '@mui/material';

import { ProductOfflineImpact } from '~/services/OrdersService';

interface Props {
  impact?: ProductOfflineImpact;
  isFetching: boolean;
  isError: boolean;
  goingOffline: boolean;
  onRetry: () => void;
}

export default function RatePlanBookingImpact({ impact, isFetching, isError, goingOffline, onRetry }: Props) {
  const hasBookings = !!(impact?.awaitingDates.total || impact?.upcomingTrips.total);
  const sample = [...(impact?.awaitingDates.sample ?? []), ...(impact?.upcomingTrips.sample ?? [])];
  const orderIds = [...new Set(sample.map((item) => item.orderId))];

  return (
    <Alert severity={isError ? 'error' : hasBookings || goingOffline ? 'warning' : 'info'} sx={{ mb: 3 }}>
      <AlertTitle>Flash rate plan · Existing customer bookings</AlertTitle>
      {isFetching ? (
        <Typography variant="body2">Checking bookings across current and past campaigns…</Typography>
      ) : isError ? (
        <Stack spacing={1}>
          <Typography variant="body2">
            Unable to check affected bookings. Retry before saving a change to Hidden or Draft.
          </Typography>
          <Button onClick={onRetry} sx={{ alignSelf: 'start' }}>
            Retry booking check
          </Button>
        </Stack>
      ) : impact ? (
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={`${impact.upcomingTrips.total} upcoming trips`} />
            <Chip size="small" label={`${impact.awaitingDates.total} Buy Now Book Later bookings awaiting dates`} />
          </Stack>
          <Typography variant="body2">
            {hasBookings
              ? 'Keep this rate plan Active so existing customers can choose or change their dates online, even after the campaign ends.'
              : 'No upcoming trips or bookings awaiting dates were found for this rate plan.'}
          </Typography>
          {orderIds.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Typography variant="body2">Sample orders:</Typography>
              {orderIds.map((orderId) => (
                <Link key={orderId} href={`/purchases/${orderId}`} target="_blank" rel="noreferrer">
                  {orderId.slice(0, 8)}
                </Link>
              ))}
            </Stack>
          )}
        </Stack>
      ) : null}
      {goingOffline && (
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'fontWeightBold' }}>
          You are taking this rate plan offline. Existing customers may lose access to booking or changing dates online.
        </Typography>
      )}
    </Alert>
  );
}
